import { Map, Marker, LngLat } from 'mapbox-gl';
import { IBoundingBox } from '../../scripts/_generated/IBoundingBox';
import { PointOfInterestMapMarkerModel } from "../../scripts/_generated/PointOfInterestMapMarkerModel";
import { FeatureCollection } from 'geojson';
import { OnboardingElementIdentifiers } from '../../scripts/_generated/OnboardingElementIdentifiers';
import { refreshActiveOnboardingStepPosition } from '../../Features/Shared/Components/Onboarding/OnboardingController';

export function getPoiMarkerPopupHtml(poi: PointOfInterestMapMarkerModel, poiDetailUrl: string) {
    const imagePart = poi.mainImageUrl ?
        `<img src="${poi.mainImageUrl}" width="200px" crossorigin="anonymous" />` :
        '<span class="text-muted"><i class="fas fa-image fa-9x"></i></span>';

    return `<div class="d-flex flex-column m-2">
                    <div><a href="${poiDetailUrl}">${imagePart}</a></div>
                    <div class="font-weight-bold" style="max-width: 200px">
                        <a href="${poiDetailUrl}"
                            class="text-decoration-none text-primary text-reset">
                            ${poi.name}
                        </a>
                    </div>
            </div>`;
}

function updateCreatePoiUrl(marker: Marker, el: HTMLDivElement, linkBuilder: (LngLat) => string) {
    const coordinates = marker.getLngLat();
    const createPoiLink = el.getElementsByClassName("create-poi").item(0) as HTMLAnchorElement;
    if (createPoiLink) {
        const createPoiUrl = linkBuilder(coordinates);
        createPoiLink.href = createPoiUrl;
    }
}

export function createPoiCreationMarker(map: Map, linkBuilder: (LngLat) => string, closeAction: () => void) {
    const el = document.createElement('div');
    el.innerHTML =
        `<div class="text-center">
                 <div class="mb-1">
                    <a href="#" class="btn btn-sm btn-primary create-poi" data-test-id="CreatePoiHereButton">Create here</a>
                    <a href="#" class="btn btn-sm btn-light cancel-create-poi"><i class="far fa-window-close"></i></a>
                 </div>
                 <div class="mb-0">
                    <i class="fas fa-map-marker fa-5x text-danger ${OnboardingElementIdentifiers.destinationsDetailMapNewPoiMarkerCssClass}" style="text-shadow: 0 0 3px #000"></i>
                 </div>
                 <div class="mt-0">
                    <i class="fas fa-crosshairs"></i>
                 </div>
             </div>
             `;


    const m = new Marker(el, {
        draggable: true,
        anchor: 'bottom'
    });
    m.setLngLat(map.getCenter());
    m.addTo(map);
    updateCreatePoiUrl(m, el, linkBuilder);

    m.on('dragend', () => {
        updateCreatePoiUrl(m, el, linkBuilder);
    });

    const closeButton = el.getElementsByClassName("cancel-create-poi").item(0) as HTMLAnchorElement;
    closeButton.onclick = (e) => {
        e.preventDefault();
        m.remove();
        if (closeAction) closeAction();
    };

    return m;
}

// force map to redraw to handle the case when map initially hidden which causes wrong initial map render
export function forceMapToRedrawWhenInitiallyHidden(target: Element, map: Map, extraActionOnRedraw: () => void = () => { }) {
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.intersectionRatio > 0) {
                map.resize();
                if (extraActionOnRedraw) extraActionOnRedraw();
                refreshActiveOnboardingStepPosition();
                observer.disconnect();
            }
        });
    },
        {
            root: document.documentElement,
        }
    );
    observer.observe(target);
}

export function addBoundingBox(map: Map, boundingBox: IBoundingBox) {
    if (map.getLayer('bbox')) {
        map.removeLayer('bbox');
    }
    if (map.getSource('bbox')) {
        map.removeSource('bbox');
    }

    const features: FeatureCollection = {
        type: 'FeatureCollection',
        features: [{
            type: 'Feature',
            properties: {},
            geometry: {
                type: 'LineString',
                coordinates: [
                    [boundingBox.northWest.longitude, boundingBox.northWest.latitude],
                    [boundingBox.northWest.longitude, boundingBox.southEast.latitude],
                    [boundingBox.southEast.longitude, boundingBox.southEast.latitude],
                    [boundingBox.southEast.longitude, boundingBox.northWest.latitude],
                    [boundingBox.northWest.longitude, boundingBox.northWest.latitude],
                ]
            }
        }]
    };

    map.addSource('bbox', {
        type: 'geojson',
        data: features,
    });

    map.addLayer({
        id: 'bbox',
        type: 'line',
        source: 'bbox',
        layout: {
            'line-join': 'round',
            'line-cap': 'round'
        },
        paint: {
            'line-color': 'gray',
            'line-width': 3,
            'line-dasharray': [3, 3]
        }
    });
}
